// Iter 375 — Conciliar movimiento bancario SOLO contra Egresos (Comprobante de Egreso) o Ingresos (Recibo de Caja)
// de Alegra disponibles (no conciliados) desde la fecha mínima configurada. Sin tipos libres.
// Iter 377 — Selección múltiple: un movimiento puede cubrir varios documentos; la suma (pesos enteros) debe igualar el monto.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, CheckCircle2, Search, Plus, Layers, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => Math.round(v || 0).toLocaleString('es-CL');
const fechaCL = (iso) => (iso ? iso.split('-').reverse().join('-') : '');
const describirCambios = (c) => Object.entries(c || {}).filter(([k]) => k !== 'facturas_cobradas' && k !== 'facturas_aplicadas')
  .map(([k, v]) => (k === 'monto' ? `monto $${CLP(v.antes)} → $${CLP(v.ahora)}` : `${k} ${v.antes ?? '—'} → ${v.ahora ?? '—'}`)).join(', ');

function DocOption({ doc, checked, onToggle, prioridad, testid, mostrarMontoDistinto, onRefrescar, refrescando }) {
  return (
    <label className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2 text-sm ${checked ? 'border-blue-400 bg-blue-50' : 'hover:bg-slate-50'}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1" data-testid={testid} />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1">
          <b>N°{doc.alegra_number || '—'}</b>
          {prioridad && <Badge variant="outline" className="text-[10px]">P{prioridad}</Badge>}
          {mostrarMontoDistinto && doc.mismo_monto === false && <Badge className="bg-amber-100 text-[10px] text-amber-800 hover:bg-amber-100">monto distinto</Badge>}
          <span className="truncate">{doc.razon_social}</span>
        </span>
        <span className="block text-xs text-muted-foreground">
          {fechaCL(doc.fecha_pago)} · <span className="font-mono">${CLP(doc.monto)}</span>{doc.oc_numero ? ` · OC ${doc.oc_numero}` : ''}{doc.descripcion ? ` · ${doc.descripcion.slice(0, 60)}` : ''}
        </span>
      </span>
      {onRefrescar && (
        <button type="button" className="mt-0.5 shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-700 disabled:opacity-50" title="Actualizar este documento desde Alegra (monto, número, estado)"
          disabled={refrescando} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRefrescar(doc); }} data-testid={`${testid}-refrescar`}>
          <RefreshCw className={`h-3.5 w-3.5 ${refrescando ? 'animate-spin' : ''}`} />
        </button>
      )}
    </label>
  );
}

function Combinaciones({ combos, docLabelPl, onUsar, seleccionActual, objetivo = 'el movimiento' }) {
  if (!combos?.length) return null;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid="seccion-combinaciones">
      <p className="mb-1 flex items-center gap-1 text-sm font-medium text-emerald-900"><Layers className="h-4 w-4" /> Combinaciones de {docLabelPl} que suman exactamente {objetivo}</p>
      <div className="space-y-1">
        {combos.map((c, i) => {
          const activa = c.ref_ids.length === seleccionActual.length && c.ref_ids.every((id) => seleccionActual.includes(id));
          return (
            <div key={c.ref_ids.join('|')} className={`flex items-center justify-between gap-2 rounded border bg-white p-2 text-xs ${activa ? 'border-emerald-500' : ''}`} data-testid={`combinacion-${i}`}>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{c.docs.map((d) => `N°${d.alegra_number || '—'} $${CLP(d.monto)}`).join(' + ')}</span>
                <span className="block truncate text-muted-foreground">{[...new Set(c.docs.map((d) => d.razon_social))].join(' / ')}{c.misma_contraparte ? '' : ' · distintas contrapartes'}</span>
              </span>
              <Button size="sm" variant={activa ? 'default' : 'outline'} className="h-7 shrink-0 px-2 text-xs" onClick={() => onUsar(c)} data-testid={`btn-usar-combinacion-${i}`}>
                {activa ? 'Seleccionada' : 'Usar'}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ConciliarMovimientoDialog({ open, mov: movProp, movs, onClose, onDone }) {
  const navigate = useNavigate();
  // Iter 380 — `movs` (varios movimientos) activa el modo grupo N:M: la suma de documentos debe igualar la suma de movimientos
  const lista = movs && movs.length ? movs : (movProp ? [movProp] : []);
  const esGrupo = lista.length > 1;
  const mov = lista[0] || null;
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [q, setQ] = useState('');
  const [sels, setSels] = useState({});
  const [nota, setNota] = useState('');
  const [destacar, setDestacar] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [refrescando, setRefrescando] = useState(null); // id del doc o 'todos'

  const load = useCallback(async (busqueda = '') => {
    if (!mov) return;
    setCargando(true);
    try {
      const r = esGrupo
        ? await api.post('/conciliacion-bancaria/grupo/sugerencias', { mov_ids: lista.map((m) => m.id), ...(busqueda ? { q: busqueda } : {}) })
        : await api.get(`/conciliacion-bancaria/movimientos/${mov.id}/sugerencias`, { params: busqueda ? { q: busqueda } : {} });
      setData((prev) => ({ ...r.data, combinaciones: busqueda ? (prev?.combinaciones || []) : (r.data.combinaciones || []) }));
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudieron cargar los documentos'); }
    finally { setCargando(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movProp, movs]);

  useEffect(() => {
    if (open) { setData(null); setQ(''); setSels({}); setNota(''); setDestacar(false); setRefrescando(null); load(); }
  }, [open, load]);

  const actualizarSel = (docNuevo) => setSels((prev) => (prev[docNuevo.id] ? { ...prev, [docNuevo.id]: { ...prev[docNuevo.id], ...docNuevo } } : prev));

  const refrescarDoc = async (doc) => {
    setRefrescando(doc.id);
    try {
      const col = data?.tipo_match === 'egreso' ? 'egresos' : 'ingresos';
      const r = await api.post(`/contabilidad/${col}/${doc.id}/refrescar-alegra`);
      const d = r.data.egreso || r.data.ingreso || {};
      const cambios = describirCambios(r.data.cambios);
      if (r.data.eliminado_en_alegra) toast.warning(`N°${doc.alegra_number || '—'} ya no existe en Alegra: quedó anulado en el ERP`);
      else if (cambios) toast.success(`N°${d.alegra_number || doc.alegra_number} actualizado desde Alegra: ${cambios}`, { duration: 7000 });
      else toast.info(`N°${doc.alegra_number || '—'} sin cambios en Alegra`);
      if (r.data.aviso_conciliacion) toast.warning(r.data.aviso_conciliacion, { duration: 8000 });
      actualizarSel({ id: doc.id, monto: d.monto, alegra_number: d.alegra_number, razon_social: d.razon_social });
      await load(q);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo actualizar desde Alegra'); }
    finally { setRefrescando(null); }
  };

  const refrescarTodos = async () => {
    const idsVisibles = [...(data?.sugerencias || []).map((s) => s.ref_id), ...(data?.disponibles || []).map((d) => d.id)];
    if (!idsVisibles.length) return;
    setRefrescando('todos');
    try {
      const r = await api.post(`/conciliacion-bancaria/movimientos/${mov.id}/refrescar-docs`, { ids: idsVisibles });
      const cambiados = (r.data.detalle || []).filter((x) => x.cambios && Object.keys(x.cambios).length);
      cambiados.forEach((x) => actualizarSel({ id: x.id, monto: x.monto, alegra_number: x.alegra_number }));
      if (cambiados.length) toast.success(`${cambiados.length} de ${r.data.revisados} documentos actualizados desde Alegra: ${cambiados.slice(0, 4).map((x) => `N°${x.alegra_number} (${describirCambios(x.cambios)})`).join('; ')}${cambiados.length > 4 ? '…' : ''}`, { duration: 9000 });
      else toast.info(`${r.data.revisados} documentos revisados en Alegra: sin cambios${r.data.errores ? ` (${r.data.errores} con error)` : ''}`);
      (r.data.detalle || []).filter((x) => x.aviso_conciliacion).forEach((x) => toast.warning(`N°${x.alegra_number}: ${x.aviso_conciliacion}`, { duration: 8000 }));
      await load(q);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo actualizar la lista desde Alegra'); }
    finally { setRefrescando(null); }
  };

  const monto = lista.reduce((t, m) => t + Math.round(m.cargo || m.abono || 0), 0);
  const seleccion = Object.values(sels);
  const ids = seleccion.map((d) => d.id);
  const suma = seleccion.reduce((s, d) => s + Math.round(d.monto || 0), 0);
  const diferencia = monto - suma;
  const multiple = seleccion.length > 1;
  const montoDistinto = !esGrupo && seleccion.length === 1 && suma !== monto;
  const sumaInvalida = (multiple || esGrupo) && seleccion.length > 0 && suma !== monto;
  const sugeridosIds = new Set((data?.sugerencias || []).map((s) => s.ref_id));
  const disponibles = (data?.disponibles || []).filter((d) => !sugeridosIds.has(d.id));
  const docLabel = data?.doc_label || (mov?.cargo > 0 ? 'Comprobante de Egreso' : 'Recibo de Caja');
  const docLabelPl = docLabel === 'Recibo de Caja' ? 'Recibos de Caja' : 'Comprobantes de Egreso';

  const toggle = (doc) => setSels((prev) => {
    const next = { ...prev };
    if (next[doc.id]) delete next[doc.id]; else next[doc.id] = doc;
    return next;
  });
  const usarCombinacion = (c) => setSels(Object.fromEntries(c.docs.map((d) => [d.id, d])));

  const handleConciliar = async () => {
    if (!seleccion.length) { toast.error(`Selecciona el ${docLabel} (o varios) con el que concilias`); return; }
    if (sumaInvalida) { toast.error(`La suma de los ${seleccion.length} documentos ($${CLP(suma)}) debe ser igual a ${esGrupo ? `la suma de los ${lista.length} movimientos` : 'el movimiento'} ($${CLP(monto)})`); return; }
    const conDestaque = destacar || montoDistinto;
    if (conDestaque && nota.trim().length < 5) { toast.error('La observación (nota) es obligatoria'); return; }
    setGuardando(true);
    try {
      const r = esGrupo
        ? await api.post('/conciliacion-bancaria/grupo/conciliar', { mov_ids: lista.map((m) => m.id), tipo: data.tipo_match, ref_ids: ids, nota, destacar: conDestaque })
        : await api.post(`/conciliacion-bancaria/movimientos/${mov.id}/conciliar`, { tipo: data.tipo_match, ref_id: ids[0], ref_ids: ids, nota, destacar: conDestaque });
      if (esGrupo) toast.success(`${r.data.movimientos} movimientos conciliados en grupo con ${r.data.docs} ${docLabelPl} ($${CLP(r.data.total)})${conDestaque ? ' · DESTACADO' : ''}`);
      else toast.success(conDestaque ? 'Movimiento conciliado y DESTACADO' : (r.data?.docs > 1 ? `Movimiento conciliado con ${r.data.docs} ${docLabelPl}` : 'Movimiento conciliado'));
      onClose(); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error conciliando'); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col" data-testid="dialog-conciliar">
        <DialogHeader><DialogTitle>{esGrupo ? `Conciliar ${lista.length} movimientos en grupo` : 'Conciliar movimiento'}</DialogTitle></DialogHeader>
        {mov && (
          <div className="-mr-2 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-2">
            {esGrupo ? (
              <div className="rounded-lg bg-slate-50 p-3 text-sm" data-testid="grupo-movimientos">
                <div className="max-h-32 space-y-0.5 overflow-y-auto">
                  {lista.map((m) => (
                    <p key={m.id} className="flex items-center gap-2 text-xs" data-testid={`grupo-mov-${m.id}`}>
                      <span className="w-20 shrink-0 text-muted-foreground">{fechaCL(m.fecha)}</span>
                      <span className="min-w-0 flex-1 truncate">{m.detalle}</span>
                      <span className={`font-mono ${m.cargo ? 'text-red-600' : 'text-emerald-600'}`}>${CLP(m.cargo || m.abono)}</span>
                    </p>
                  ))}
                </div>
                <p className="mt-2 border-t pt-2 text-right text-sm font-semibold" data-testid="grupo-total">Total {mov.cargo ? 'cargos' : 'abonos'}: <span className="font-mono">${CLP(monto)}</span></p>
                <p className="text-xs text-muted-foreground">Los {docLabelPl} seleccionados deben sumar exactamente este total. Deshacer cualquiera de estos movimientos deshace el grupo completo.</p>
              </div>
            ) : (
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="font-medium">{mov.detalle}</p>
                <p className="text-xs text-muted-foreground">{fechaCL(mov.fecha)} · {mov.cargo ? `Cargo $${CLP(mov.cargo)}` : `Abono $${CLP(mov.abono)}`}</p>
              </div>
            )}

            {!esGrupo && mov.abono > 0 && data?.facturas_venta_abiertas?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="aviso-factura-venta-abierta">
                <b>Factura de venta abierta por este monto sin Recibo de Caja en Alegra:</b>{' '}
                {data.facturas_venta_abiertas.map((f) => `N°${f.numero} ${f.cliente_nombre} (${fechaCL(f.fecha)})`).join('; ')}.
                {' '}Regístralo con <b>Registrar Recibo de Caja</b> (abajo) o desde <b>Pagos de Clientes (VB)</b>.
              </div>
            )}
            {!esGrupo && mov.abono > 0 && (
              <Button variant="outline" size="sm" className="w-full justify-start border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => { onClose(); navigate(`/contabilidad/ingresos?nuevo=1&movimiento=${mov.id}`); }} data-testid="btn-registrar-recibo-caja">
                <Plus className="mr-1 h-4 w-4" /> Registrar Recibo de Caja en Alegra para este abono (facturas del cliente u otra cuenta) y conciliar
              </Button>
            )}

            <Combinaciones combos={data?.combinaciones} docLabelPl={docLabelPl} onUsar={usarCombinacion} seleccionActual={ids} objetivo={esGrupo ? `la suma de los ${lista.length} movimientos` : 'el movimiento'} />

            {data?.sugerencias?.length > 0 && (
              <div data-testid="seccion-sugerencias">
                <p className="mb-1 text-sm font-medium">Sugerencias ({docLabelPl} con el mismo monto)</p>
                <div className="space-y-1">
                  {data.sugerencias.map((s, i) => {
                    const doc = { ...s.doc_resumen, id: s.ref_id, mismo_monto: true };
                    return <DocOption key={s.ref_id} doc={doc} prioridad={s.prioridad} checked={!!sels[s.ref_id]} onToggle={() => toggle(doc)} testid={`sugerencia-${i}`}
                      onRefrescar={refrescarDoc} refrescando={refrescando === s.ref_id || refrescando === 'todos'} />;
                  })}
                </div>
              </div>
            )}

            <div data-testid="seccion-disponibles">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{docLabelPl} disponibles en Alegra <span className="text-xs font-normal text-muted-foreground">(desde {fechaCL(data?.fecha_min_docs)}, no conciliados · marca varios si el movimiento cubre más de uno)</span></p>
                <div className="flex items-center gap-1">
                  <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(q)}
                    placeholder="N°, nombre, RUT o monto…" className="h-7 w-48 text-xs" data-testid="input-buscar-doc" />
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => load(q)} data-testid="btn-buscar-doc"><Search className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-sky-700" onClick={refrescarTodos} disabled={!!refrescando || cargando || !data}
                    title="Releer desde Alegra todos los documentos listados (montos/números modificados allá)" data-testid="btn-refrescar-docs">
                    <RefreshCw className={`h-3.5 w-3.5 ${refrescando === 'todos' ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              {cargando ? (
                <p className="flex items-center gap-2 p-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Cargando documentos…</p>
              ) : disponibles.length === 0 ? (
                <p className="rounded border border-dashed p-2 text-xs text-muted-foreground" data-testid="disponibles-empty">
                  No hay {docLabelPl} disponibles{q ? ` para "${q}"` : ''}. Si el pago no está en Alegra, regístralo primero
                  {mov.abono > 0 ? ' (Registrar Recibo de Caja, Pagos de Clientes (VB) o Ingreso exprés)' : ' (Egreso exprés en Cruce Alegra)'}.
                </p>
              ) : (
                <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
                  {disponibles.map((d) => (
                    <DocOption key={d.id} doc={d} checked={!!sels[d.id]} onToggle={() => toggle(d)} testid={`disponible-${d.alegra_number || d.id}`} mostrarMontoDistinto={!multiple}
                      onRefrescar={refrescarDoc} refrescando={refrescando === d.id || refrescando === 'todos'} />
                  ))}
                </div>
              )}
            </div>

            {seleccion.length > 0 && (
              <div className={`rounded px-2 py-1 text-xs font-medium ${sumaInvalida ? 'bg-amber-100 text-amber-800' : (suma === monto ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700')}`} data-testid="resumen-seleccion">
                {seleccion.length} {seleccion.length === 1 ? docLabel : docLabelPl} seleccionado{seleccion.length === 1 ? '' : 's'}: <span className="font-mono">${CLP(suma)}</span> · {esGrupo ? `${lista.length} movimientos` : 'movimiento'} <span className="font-mono">${CLP(monto)}</span>
                {suma === monto ? ' — calza ✓' : (diferencia > 0 ? ` — faltan $${CLP(diferencia)}` : ` — sobran $${CLP(-diferencia)}`)}
              </div>
            )}
            {sumaInvalida && (
              <p className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800" data-testid="aviso-suma-invalida">
                {esGrupo ? 'En un grupo la suma de los documentos debe ser exactamente igual a la suma de los movimientos (en pesos).' : 'Con varios documentos la suma debe ser exactamente igual al movimiento (en pesos).'} Agrega o quita documentos hasta que calce.
              </p>
            )}
            {montoDistinto && (
              <p className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800" data-testid="aviso-monto-distinto">
                El {docLabel} seleccionado es por ${CLP(suma)} y el movimiento por ${CLP(monto)}: se conciliará DESTACADO con tu observación.
                {diferencia > 0 ? ` Si el depósito cubre más documentos, márcalos también hasta completar los $${CLP(monto)}.` : ''}
              </p>
            )}
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} data-testid="input-nota"
              placeholder={destacar || montoDistinto ? 'Observación OBLIGATORIA (quedará visible y destacada)' : 'Nota (opcional)'} />
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="label-destacar">
              <input type="checkbox" checked={destacar || !!montoDistinto} disabled={!!montoDistinto} onChange={(e) => setDestacar(e.target.checked)} className="mt-0.5" data-testid="check-destacar" />
              <span><b>Conciliar con observación / destacar</b> — el movimiento quedará marcado ⚠ de forma permanente con tu explicación (ej. pago duplicado, monto distinto, error del banco).</span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-cancelar-conciliar">Cancelar</Button>
          <Button onClick={handleConciliar} disabled={guardando || !seleccion.length || sumaInvalida} data-testid="btn-confirmar-conciliar">
            {guardando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
            Conciliar{multiple ? ` (${seleccion.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
